import {
  toastMessages,
  showOperationResult,
  showBulkOperationResult,
  showProgressToast,
  updateProgressToast,
} from '@/utils/toastMessages'
import type { BulkOperationResult, ToastOptions } from '@/utils/toastMessages'

/**
 * Unified toast notification hook for VRP System v4
 * Direct function references (stable by React design) - zero allocations per render
 * Replaces useToastNotifications wrapper with direct exports from toastMessages
 */
export interface UseToastReturn {
  notifyEntityCreated: typeof toastMessages.success.created
  notifyEntityUpdated: typeof toastMessages.success.updated
  notifyEntityDeleted: typeof toastMessages.success.deleted
  notifyEntityCloned: typeof toastMessages.success.cloned
  notifyOperationResult: typeof showOperationResult
  notifyBulkOperation: typeof showBulkOperationResult
  notifyImportResult: typeof toastMessages.success.imported
  notifyExportResult: typeof toastMessages.success.exported
  notifyImportError: typeof toastMessages.error.importFailed
  notifyExportError: typeof toastMessages.error.exportFailed
  notifyValidationError: typeof toastMessages.error.validationFailed
  notifyNetworkError: typeof toastMessages.error.networkError
  notifyUnauthorized: typeof toastMessages.error.unauthorized
  notifyUnsavedChanges: typeof toastMessages.warning.unsavedChanges
  notifyDataIncomplete: typeof toastMessages.warning.dataIncomplete
  notifyCascadeEffect: typeof toastMessages.warning.cascadeEffect
  startProgress: typeof showProgressToast
  updateProgress: typeof updateProgressToast
  notifySuccess: typeof toastMessages.success.generic
  notifyError: typeof toastMessages.error.generic
  notifyWarning: typeof toastMessages.warning.generic
  notifyInfo: typeof toastMessages.info.generic
}

/**
 * Primary toast hook - returns stable function references
 */
export const useToast = (): UseToastReturn => {
  return {
    notifyEntityCreated: toastMessages.success.created,
    notifyEntityUpdated: toastMessages.success.updated,
    notifyEntityDeleted: toastMessages.success.deleted,
    notifyEntityCloned: toastMessages.success.cloned,
    notifyOperationResult: showOperationResult,
    notifyBulkOperation: showBulkOperationResult,
    notifyImportResult: toastMessages.success.imported,
    notifyExportResult: toastMessages.success.exported,
    notifyImportError: toastMessages.error.importFailed,
    notifyExportError: toastMessages.error.exportFailed,
    notifyValidationError: toastMessages.error.validationFailed,
    notifyNetworkError: toastMessages.error.networkError,
    notifyUnauthorized: toastMessages.error.unauthorized,
    notifyUnsavedChanges: toastMessages.warning.unsavedChanges,
    notifyDataIncomplete: toastMessages.warning.dataIncomplete,
    notifyCascadeEffect: toastMessages.warning.cascadeEffect,
    startProgress: showProgressToast,
    updateProgress: updateProgressToast,
    notifySuccess: toastMessages.success.generic,
    notifyError: toastMessages.error.generic,
    notifyWarning: toastMessages.warning.generic,
    notifyInfo: toastMessages.info.generic,
  }
}

export type { BulkOperationResult, ToastOptions } from '@/utils/toastMessages'
